import type { NominatimPlace } from "@/lib/nominatim";

const GRANULAR_LOCATION_TYPES = new Set([
  "house",
  "building",
  "residential",
  "road",
  "tertiary",
  "secondary",
  "primary",
  "service",
  "footway",
  "pedestrian",
  "cycleway",
  "path",
  "station",
  "stop",
  "bus_stop",
  "tram_stop",
  "platform",
  "commercial",
  "industrial",
  "parking",
]);

export function formatLocalityDisplayName(place: NominatimPlace): string {
  const primaryLabel = getLocalityPrimaryLabel(place);
  const city = cleanLocationSegment(place.address.city);
  const state = cleanLocationSegment(place.address.state);
  const country = cleanLocationSegment(place.address.country);

  return dedupeLocationSegments([primaryLabel, state ?? city, country]).join(
    ", ",
  );
}

export function sanitizeLocalityPlace(place: NominatimPlace): NominatimPlace {
  const primaryLabel = getLocalityPrimaryLabel(place);
  const city = cleanLocationSegment(place.address.city);

  return {
    ...place,
    name: primaryLabel,
    display_name: primaryLabel,
    address: {
      ...place.address,
      city,
      suburb: primaryLabel !== city ? primaryLabel : place.address.suburb,
    },
  };
}

export function getLocalityPrimaryLabel(place: NominatimPlace): string {
  return getLocalityPrimaryLabelInternal(place);
}

export function cleanLocationSegment(
  value?: string | null,
): string | undefined {
  const trimmedValue = value?.trim();
  if (!trimmedValue) {
    return undefined;
  }

  return trimmedValue.replace(/\s+/g, " ");
}

function getLocalityPrimaryLabelInternal(place: NominatimPlace): string {
  const suburb = cleanLocationSegment(place.address.suburb);
  const city = cleanLocationSegment(place.address.city);
  const state = cleanLocationSegment(place.address.state);
  const country = cleanLocationSegment(place.address.country);
  const displayPrimary = cleanLocationSegment(
    place.display_name.split(",")[0] ?? undefined,
  );
  const name = cleanLocationSegment(place.name);

  if (isGranularLocation(place)) {
    return (
      suburb ?? city ?? state ?? country ?? displayPrimary ?? "Selected area"
    );
  }

  return (
    name ??
    suburb ??
    city ??
    displayPrimary ??
    state ??
    country ??
    "Selected area"
  );
}

function isGranularLocation(place: NominatimPlace): boolean {
  return (
    GRANULAR_LOCATION_TYPES.has(place.type.trim().toLowerCase()) ||
    Boolean(cleanLocationSegment(place.address.road))
  );
}

function dedupeLocationSegments(input: Array<string | undefined>): string[] {
  const seen = new Set<string>();
  const deduped: string[] = [];

  for (const segment of input) {
    const trimmedSegment = segment?.trim();
    const normalized = trimmedSegment?.toLowerCase();
    if (!normalized || seen.has(normalized)) {
      continue;
    }
    if (trimmedSegment === undefined) {
      continue;
    }

    seen.add(normalized);
    deduped.push(trimmedSegment);
  }

  return deduped;
}
